import { useEffect, useState, useCallback } from "react";
import { api, SEBA_IMG, ratingFromKnots, ratingMeta } from "@/lib/api";
import { Header } from "@/components/Header";
import { SpotBar } from "@/components/SpotBar";
import { LiveDashboard } from "@/components/LiveDashboard";
import { WindChart } from "@/components/WindChart";
import { Forecast } from "@/components/Forecast";
import { UpdatesFeed } from "@/components/UpdatesFeed";
import { CommunityBoard } from "@/components/CommunityBoard";
import { WebcamCard } from "@/components/WebcamCard";
import { WindguruWidget } from "@/components/WindguruWidget";
import { AnimatedMaps } from "@/components/AnimatedMaps";
import { RadarMap } from "@/components/RadarMap";
import { VerdictBanner } from "@/components/VerdictBanner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Wind, RefreshCw, Bell, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const FAV_KEY = "meteoseba_favs";

export default function Home() {
  const [spots, setSpots] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [weather, setWeather] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phrase, setPhrase] = useState("");
  const [verdict, setVerdict] = useState(null);
  const [tab, setTab] = useState("live");
  const [community, setCommunity] = useState([]);
  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(FAV_KEY) || "[]");
    } catch {
      return [];
    }
  });

  const activeSpot = spots.find((s) => s.id === activeId);

  const loadSpots = useCallback(async () => {
    const { data } = await api.get("/spots");
    setSpots(data);
    if (data.length && !activeId) {
      const preferred = data.find((s) => /malcesine/i.test(s.name));
      setActiveId((preferred || data[0]).id);
    }
  }, [activeId]);

  const loadWeather = useCallback(async (spot) => {
    if (!spot) return;
    setLoading(true);
    try {
      const { data } = await api.get("/weather", {
        params: { lat: spot.latitude, lon: spot.longitude },
      });
      setWeather(data);
      const kn = data.current?.wind_speed;
      const rating = ratingFromKnots(kn);
      if (rating.key === "good" || rating.key === "strong") {
        toast.success(`${spot.name}: condizioni ${rating.label}! ${Math.round(kn)} nodi 🏄`, {
          icon: <Bell className="h-4 w-4" />,
        });
      }
    } catch {
      toast.error("Impossibile caricare i dati meteo");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUpdates = useCallback(async () => {
    const { data } = await api.get("/updates");
    setUpdates(data);
  }, []);

  useEffect(() => {
    loadSpots();
    loadUpdates();
    api.get("/phrase").then((r) => setPhrase(r.data.phrase)).catch(() => {});
    api.get("/verdict").then((r) => setVerdict(r.data)).catch(() => {});
    api.get("/community").then((r) => setCommunity(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (activeSpot) {
      loadWeather(activeSpot);
    }
  }, [activeId]); // eslint-disable-line

  const toggleFav = (id) => {
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem(FAV_KEY, JSON.stringify(next));
      return next;
    });
  };

  const sortedSpots = [...spots].sort((a, b) => {
    const fa = favorites.includes(a.id) ? 0 : 1;
    const fb = favorites.includes(b.id) ? 0 : 1;
    return fa - fb;
  });

  return (
    <div className="min-h-screen">
      <Header />

      <VerdictBanner verdict={verdict} knots={weather?.current?.wind_speed} />

      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-sky-700 to-cyan-600">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 flex items-center gap-5 relative z-10">
          <img
            src={SEBA_IMG}
            alt="Seba"
            className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl object-cover ring-4 ring-white/30 shadow-xl"
            data-testid="seba-hero-img"
          />
          <div className="text-white">
            <div className="flex items-center gap-2 mb-1">
              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-white/80">
                Meteo Torbole Live
              </span>
            </div>
            <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">
              Il vento del Garda, in diretta
            </h1>
            <p className="text-white/80 text-sm mt-1 max-w-md">
              Dati live + gli aggiornamenti sul campo di Seba per windsurfisti... e forse winger.
            </p>
          </div>
        </div>
        <Wind className="absolute -right-8 -bottom-8 h-52 w-52 text-white/10" />
      </div>

      {/* Frase del giorno di Seba */}
      {phrase && (
        <div className="bg-slate-900" data-testid="phrase-of-day">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
            <Quote className="h-5 w-5 text-orange-400 shrink-0" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-orange-400 shrink-0 hidden sm:inline">
              Frase del giorno
            </span>
            <p className="font-head font-semibold text-white text-sm sm:text-base italic truncate">
              "{phrase}"
            </p>
            <span className="ml-auto text-xs text-slate-400 shrink-0 hidden sm:inline">— Seba</span>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <SpotBar
          spots={sortedSpots}
          activeId={activeId}
          onSelect={setActiveId}
          favorites={favorites}
          onToggleFav={toggleFav}
        />

        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <TabsList className="bg-white border border-slate-200 rounded-full p-1 h-auto" data-testid="main-tabs">
            <TabsTrigger value="live" className="rounded-full px-5 py-2 data-[state=active]:bg-sky-600 data-[state=active]:text-white font-head font-semibold" data-testid="tab-live">
              Vento Live
            </TabsTrigger>
            <TabsTrigger value="feed" className="rounded-full px-5 py-2 data-[state=active]:bg-sky-600 data-[state=active]:text-white font-head font-semibold" data-testid="tab-feed">
              Bacheca di Seba
            </TabsTrigger>
            <TabsTrigger value="community" className="rounded-full px-5 py-2 data-[state=active]:bg-orange-500 data-[state=active]:text-white font-head font-semibold" data-testid="tab-community">
              Bothra Boys
            </TabsTrigger>
          </TabsList>

          {updates[0] && (
            <button
              onClick={() => setTab("feed")}
              data-testid="latest-update-strip"
              className="group mt-4 w-full flex items-center gap-3 bg-white border border-slate-200 rounded-2xl px-4 py-3 text-left hover:border-sky-300 hover:shadow-sm transition-all duration-200"
            >
              <img src={SEBA_IMG} alt="Seba" className="h-10 w-10 rounded-full object-cover ring-2 ring-sky-100 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-sky-600">Ultimo dalla Bacheca</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600`}>
                    {ratingMeta(updates[0].wind_rating).label}
                  </span>
                </div>
                <p className="font-head font-semibold text-slate-900 truncate">{updates[0].title}</p>
                <p className="text-sm text-slate-500 truncate">{updates[0].note}</p>
              </div>
              <span className="shrink-0 text-xs font-semibold text-sky-600 group-hover:translate-x-0.5 transition-transform">Apri →</span>
            </button>
          )}

          {community[0] && (
            <button
              onClick={() => setTab("community")}
              data-testid="latest-community-strip"
              className="group mt-3 w-full flex items-center gap-3 bg-white border border-slate-200 rounded-2xl px-4 py-3 text-left hover:border-orange-300 hover:shadow-sm transition-all duration-200"
            >
              <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-head font-bold shrink-0">
                {(community[0].author || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-orange-500">Ultimo dai Bothra Boys</span>
                  {community[0].spot_name && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{community[0].spot_name}</span>
                  )}
                </div>
                <p className="font-head font-semibold text-slate-900 truncate">{community[0].author}</p>
                <p className="text-sm text-slate-500 truncate">{community[0].message}</p>
              </div>
              <span className="shrink-0 text-xs font-semibold text-orange-500 group-hover:translate-x-0.5 transition-transform">Apri →</span>
            </button>
          )}

          <TabsContent value="live" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-head text-2xl sm:text-3xl font-bold text-slate-900">
                {activeSpot?.name || "Spot"}
              </h2>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={() => loadWeather(activeSpot)}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>

            {loading && !weather ? (
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <Skeleton className="sm:col-span-2 sm:row-span-2 h-64 rounded-3xl" />
                <Skeleton className="sm:col-span-2 sm:row-span-2 h-64 rounded-3xl" />
                <Skeleton className="sm:col-span-2 h-24 rounded-3xl" />
                <Skeleton className="sm:col-span-2 h-24 rounded-3xl" />
              </div>
            ) : weather ? (
              <>
                <LiveDashboard current={weather.current} />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                  <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-100 shadow-sm p-5">
                    <h3 className="font-head text-xl font-semibold text-slate-900 mb-2">
                      Vento orario (prossime 24h)
                    </h3>
                    <WindChart hourly={weather.hourly} />
                  </div>
                  <Forecast daily={weather.daily} />
                </div>
              </>
            ) : (
              <div
                data-testid="weather-error-card"
                className="bg-white rounded-3xl border border-slate-200 p-6 text-center"
              >
                <p className="font-head font-semibold text-slate-900">
                  Dati vento momentaneamente non disponibili
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  Riprova con il pulsante di aggiornamento. Intanto qui sotto trovi Windguru, mappe e webcam.
                </p>
              </div>
            )}

            {activeSpot && (
              <>
                <div className="mt-4">
                  <WindguruWidget spot={activeSpot} />
                </div>
                <div className="mt-4">
                  <AnimatedMaps spot={activeSpot} />
                </div>
                <div className="mt-4">
                  <WebcamCard spot={activeSpot} />
                </div>
                <div className="mt-4">
                  <RadarMap spot={activeSpot} />
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="feed" className="mt-6">
            <UpdatesFeed updates={updates} />
          </TabsContent>

          <TabsContent value="community" className="mt-6">
            <CommunityBoard spots={sortedSpots} activeSpotId={activeId} />
          </TabsContent>
        </Tabs>
      </main>

      <footer className="max-w-6xl mx-auto px-4 sm:px-6 py-8 text-center text-xs text-slate-400 space-y-1">
        <p>MeteoSeba · Dati vento live via Open-Meteo · Aggiornamenti a cura di Seba</p>
        <p data-testid="site-credit" className="font-medium text-slate-500">design sito: Leonardo Brighenti</p>
      </footer>
    </div>
  );
}
